import { createContext, useContext, useEffect, useState } from 'react';
import { supabase, supabaseConfigured } from './supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadProfile(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) loadProfile(session.user.id);
      else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    setProfile(data);
    setLoading(false);
  }

  async function signIn(email, password) {
    if (!supabaseConfigured) {
      throw new Error(
        'Supabase credentials missing from build. Copy frontend/.env.example to frontend/.env and rebuild.'
      );
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }

  async function signUp(email, password, name, role = 'runner', extras = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          handle: name?.toLowerCase().replace(/\s+/g, '_'),
          role,
          ...(extras.firstName ? { first_name: extras.firstName } : {}),
          ...(extras.lastName ? { last_name: extras.lastName } : {}),
          ...(extras.birthday ? { birthday: extras.birthday } : {}),
        },
      },
    });
    if (error) throw error;
    return data;
  }

  async function signOut() {
    if (!supabaseConfigured) return;
    await supabase.auth.signOut();
  }

  async function refreshProfile() {
    if (!user?.id) return;
    await loadProfile(user.id);
  }

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    refreshProfile,
    supabaseEnabled: supabaseConfigured,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
